import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { Badge, Button, Divider, Image } from 'antd';
import { DownloadOutlined, FileOutlined, LoadingOutlined, PlusCircleOutlined, SendOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { useWatiChat } from '../contexts/WatiChatContext';
import { useWatiSocket } from '../contexts/WatiSocketContext';
import { useAuth } from '../contexts/AuthContext';
import { useCommonApiCall } from '../Api/commonApiCall';
import { API_DOMAIN } from '../constants';
import person_avatar from '../../assets/person_avatar.svg';
import loading_circle_eclipse from '../../assets/images/tube-spinner.svg';

const CHANNEL_ID = '68d251023d3de3334068a1d9';
const SEND_ENDPOINT = `${API_DOMAIN}/whatsapp/send_wati_general_message`;

// Normalise a raw entry from moredetails.success to a plain phone number string
const toPhone = (e) =>
    (e?.phoneNumber || (typeof e === 'string' ? e : '')).replace('@c.us', '');

const STATUS_META = {
    SENDING: { color: '#9ca3af', icon: '⏱' },
    SENT: { color: '#6b7280', icon: '✓' },
    DELIVERED: { color: '#3b82f6', icon: '✓✓' },
    READ: { color: '#22c55e', icon: '✓✓' },
    FAILED: { color: '#ef4444', icon: '✗' },
};

const renderStatus = (statusString) => {
    if (!statusString) return null;
    const meta = STATUS_META[statusString.toUpperCase()] || { color: '#9ca3af', icon: '' };
    return <span style={{ color: meta.color, fontWeight: 600, marginLeft: 2 }}>{meta.icon} {statusString}</span>;
};

const formatChatMessageTime = (epoch) => {
    if (!epoch) return null;
    const date = new Date(epoch * 1000);
    const now = new Date();
    const isToday = date.getDate() === now.getDate() && date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
    const timeOpts = { hour: 'numeric', minute: 'numeric', hour12: true };
    return isToday
        ? new Intl.DateTimeFormat('default', timeOpts).format(date)
        : new Intl.DateTimeFormat('default', { month: 'short', day: 'numeric', ...timeOpts }).format(date);
};

const formatWhatsAppText = (text) => {
    if (!text) return null;
    const regex = /(\*.*?\*|__.*?__|~.*?~|```.*?```)/g;
    return text.split(regex).map((part, i) => {
        if (!part) return null;
        if (part.startsWith('```') && part.endsWith('```')) return <code key={i} className="bg-gray-100 p-0.5 rounded font-mono text-xs">{part.slice(3, -3)}</code>;
        if (part.startsWith('*') && part.endsWith('*')) return <strong key={i}>{part.slice(1, -1)}</strong>;
        if (part.startsWith('__') && part.endsWith('__')) return <em key={i}>{part.slice(2, -2)}</em>;
        if (part.startsWith('_') && part.endsWith('_')) return <em key={i}>{part.slice(1, -1)}</em>;
        if (part.startsWith('~') && part.endsWith('~')) return <span key={i} className="line-through text-gray-400">{part.slice(1, -1)}</span>;
        return part;
    });
};

const QueryChatMini = ({ record, supplierSet }) => {
    const navigate = useNavigate();
    const { chatlist, setChatlist } = useWatiChat();
    const { watiChatUpdates } = useWatiSocket();
    const { authState } = useAuth();
    const { CommonApiCall } = useCommonApiCall();

    // Derive the set of phone numbers for this record
    const supplierPhones = (() => {
        let raw = record?.moredetails?.success || '[]';
        try { raw = typeof raw === 'string' ? JSON.parse(raw) : raw; } catch { raw = []; }
        return Array.isArray(raw) ? raw.map(toPhone).filter(Boolean) : [];
    })();

    // Filter global chatlist to only entries matching this record's suppliers
    const filteredChats = chatlist.filter(c => supplierPhones.includes(c.whatsappId));

    const [selectedChatId, setSelectedChatId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [chatWindowLoading, setChatWindowLoading] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [loadingOlder, setLoadingOlder] = useState(false);
    const [messageText, setMessageText] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [downloadingId, setDownloadingId] = useState(null);
    const [flashingId, setFlashingId] = useState(null);

    const selectedChatRef = useRef(null);
    const scrollBottomRef = useRef(null);
    const scrollRestoreRef = useRef(null);
    const messageContainerRef = useRef(null);
    const messageRefsMap = useRef({});
    const pendingTempMap = useRef(new Map());
    const watiProcessedCountRef = useRef(watiChatUpdates.length); // start from current

    // Keep ref in sync
    useEffect(() => { selectedChatRef.current = selectedChatId; }, [selectedChatId]);

    // Restore scroll position after prepend
    useLayoutEffect(() => {
        if (scrollRestoreRef.current !== null && messageContainerRef.current) {
            const newH = messageContainerRef.current.scrollHeight;
            messageContainerRef.current.scrollTop = newH - scrollRestoreRef.current;
            scrollRestoreRef.current = null;
        }
    }, [messages]);

    // Socket: live message updates for the selected chat
    useEffect(() => {
        if (watiChatUpdates.length <= watiProcessedCountRef.current) return;
        const newUpdates = watiChatUpdates.slice(watiProcessedCountRef.current);
        watiProcessedCountRef.current = watiChatUpdates.length;

        newUpdates.forEach(update => {
            if (update.target === 'sendTextMessage') {
                const convId = update['0']?.conversationId;
                if (convId !== selectedChatRef.current) return;
                const isMedia = ['image', 'video', 'audio'].includes(update['0'].messageType);
                const isDoc = update['0'].messageType === 'document';
                const msg = {
                    id: update['0'].messageId,
                    type: update['0'].messageType,
                    text: update['0'].message.text,
                    owner: update['0'].message.isOwner,
                    statusString: update['0'].message.statusString,
                    timestamp: parseInt(update['0'].timestamp),
                    replySourceMessage: update['0'].replySourceMessage,
                    ...(isMedia && { data: null, data_original: update['0'].message.data }),
                    ...(isDoc && { data_original: update['0'].message.data }),
                };
                setMessages(prev => {
                    const localId = update['0'].localMessageId;
                    const tempId = localId ? pendingTempMap.current.get(localId) : undefined;
                    if (tempId) {
                        pendingTempMap.current.delete(localId);
                        const idx = prev.findIndex(m => m.id === tempId);
                        if (idx !== -1) { const n = [...prev]; n[idx] = msg; return n; }
                    }
                    if (prev.some(m => m.id === msg.id)) return prev;
                    return [...prev, msg];
                });
                if (isMedia && update['0'].message.data) handleMediaNeedLoad(update['0'].messageId, update['0'].message.data);
                scrollIfNearBottom();
            }

            if (update.target === 'updatingOperatorMessageStatus') {
                const d = Array.isArray(update['0']) ? update['0'][0] : update['0'];
                if (d && selectedChatRef.current === d.conversationId) {
                    setMessages(prev => prev.map(m => m.id === d.id ? { ...m, statusString: d.statusString } : m));
                }
            }

            if (update.target === 'messageFromOperator') {
                const msg = update['0'];
                if (selectedChatRef.current !== msg?.conversationId) return;
                const isMedia = ['image', 'video', 'audio'].includes(msg.type);
                const isDoc = msg.type === 'document';
                const newMsg = {
                    id: msg.id, localMessageId: msg.localMessageId, conversationId: msg.conversationId,
                    type: msg.type, text: msg.text, time: msg.time, owner: true, isOwner: true,
                    statusString: msg.statusString, timestamp: parseInt(msg.time),
                    operatorName: msg.operatorName, replySourceMessage: msg.replyContextId || null,
                    ...(isMedia && { data: null, data_original: msg.media }),
                    ...(isDoc && { data_original: msg.media }),
                };
                setMessages(prev => {
                    const localId = msg.localMessageId;
                    const tempId = localId ? pendingTempMap.current.get(localId) : undefined;
                    if (tempId) {
                        pendingTempMap.current.delete(localId);
                        const idx = prev.findIndex(m => m.id === tempId);
                        if (idx !== -1) { const n = [...prev]; n[idx] = newMsg; return n; }
                    }
                    if (prev.some(m => m.id === newMsg.id)) return prev;
                    return [...prev, newMsg];
                });
                scrollIfNearBottom();
            }
        });
    }, [watiChatUpdates]);

    const scrollIfNearBottom = () => {
        setTimeout(() => {
            if (messageContainerRef.current) {
                const { scrollTop, scrollHeight, clientHeight } = messageContainerRef.current;
                if (scrollHeight - scrollTop - clientHeight < 300) scrollToBottom('smooth');
            }
        }, 50);
    };

    const scrollToBottom = (behavior = 'auto') => {
        scrollBottomRef.current?.scrollIntoView({ behavior });
    };

    const scrollToMessage = (messageId) => {
        const el = messageRefsMap.current[messageId];
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setFlashingId(messageId);
        setTimeout(() => setFlashingId(null), 1500);
    };

    const selectChat = async (chatId) => {
        if (chatId === selectedChatRef.current) return;
        setSelectedChatId(chatId);
        setChatWindowLoading(true);
        setHasMore(false);
        setMessages([]);
        try {
            const response = await CommonApiCall({ chatId, lastMessageId: null }, 'wati_messages/fetch_specific_chat_messages', 'post');
            if (response.status) {
                const exchanges = response.data.data.reverse().map(msg => {
                    if (['image', 'video', 'audio'].includes(msg.type)) return { ...msg, data_original: msg.data_original || msg.data, data: null };
                    if (msg.type === 'document') return { ...msg, data_original: msg.data_original || msg.data };
                    return msg;
                });
                setMessages(exchanges);
                setHasMore(exchanges.length > 0);
                [...exchanges].reverse().forEach(msg => {
                    if (['image', 'video', 'audio'].includes(msg.type) && msg.data_original) handleMediaNeedLoad(msg.id, msg.data_original);
                });
                setChatlist(prev => prev.map(c => c.id === chatId ? { ...c, unreadCount: 0 } : c));
            }
        } catch { /* silent */ }
        finally {
            setChatWindowLoading(false);
            setTimeout(() => scrollToBottom('auto'), 100);
            await CommonApiCall({ chatId }, 'wati_messages/mark_chat_as_read', 'post');
        }
    };

    const loadOlderMessages = async () => {
        if (!hasMore || loadingOlder || !selectedChatRef.current) return;
        setLoadingOlder(true);
        try {
            const oldestId = messages[0]?.id ?? null;
            const response = await CommonApiCall({ chatId: selectedChatRef.current, lastMessageId: oldestId }, 'wati_messages/fetch_specific_chat_messages', 'post');
            if (response.status) {
                const older = (response.data.data ?? []).reverse().map(msg => {
                    if (['image', 'video', 'audio'].includes(msg.type)) return { ...msg, data_original: msg.data_original || msg.data, data: null };
                    if (msg.type === 'document') return { ...msg, data_original: msg.data_original || msg.data };
                    return msg;
                });
                if (!older.length) { setHasMore(false); return; }
                if (messageContainerRef.current) scrollRestoreRef.current = messageContainerRef.current.scrollHeight;
                setMessages(prev => [...older, ...prev]);
                older.forEach(msg => {
                    if (['image', 'video', 'audio'].includes(msg.type) && msg.data_original) handleMediaNeedLoad(msg.id, msg.data_original);
                });
            }
        } catch { /* silent */ }
        finally { setLoadingOlder(false); }
    };

    const handleMediaNeedLoad = async (msgId, link) => {
        const response = await CommonApiCall({ file_name: link }, 'wati_messages/parse_media_file_url', 'post');
        if (response.status) setMessages(prev => prev.map(m => m.id === msgId ? { ...m, data: response.data } : m));
    };

    const handleDownload = async (url, fileName, msgId) => {
        if (downloadingId) return;
        setDownloadingId(msgId);
        try {
            const response = await axios.post(`${API_DOMAIN}/wati_messages/download_wati_media_file`, { file_name: url }, { responseType: 'blob' });
            const blobUrl = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = blobUrl; link.download = fileName || 'uzdpart_media.jpg';
            document.body.appendChild(link); link.click(); link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch { /* silent */ }
        finally { setDownloadingId(null); }
    };

    const handleSend = async () => {
        if (!messageText.trim() || isSending || !selectedChatId) return;
        const phone = chatlist.find(c => c.id === selectedChatId)?.whatsappId;
        if (!phone) return;
        const sentText = messageText;
        const tempId = 'temp_' + Date.now();
        const localMessageId = uuidv4();
        pendingTempMap.current.set(localMessageId, tempId);
        setMessages(prev => [...prev, {
            id: tempId, localMessageId, conversationId: selectedChatId, type: 'text',
            text: sentText, owner: true, isOwner: true, statusString: 'SENDING',
            timestamp: Math.floor(Date.now() / 1000), operatorName: authState.user?.name,
        }]);
        setMessageText('');
        setIsSending(true);
        setTimeout(() => scrollToBottom('smooth'), 50);
        try {
            await axios.post(SEND_ENDPOINT, { phone, message: sentText, user: authState.user?.name, channelId: CHANNEL_ID, localMessageId });
            const sentAt = Math.floor(Date.now() / 1000);
            setMessages(prev => prev.map(m => m.id === tempId ? { ...m, statusString: 'SENT' } : m));
            setChatlist(prev => [...prev.map(c => c.id === selectedChatId ? { ...c, text: sentText, lastMessageDate: sentAt, lastUpdated: String(sentAt) } : c)]
                .sort((a, b) => parseFloat(b.lastUpdated) - parseFloat(a.lastUpdated)));
        } catch {
            pendingTempMap.current.delete(localMessageId);
            setMessages(prev => prev.filter(m => m.id !== tempId));
            setMessageText(sentText);
        } finally { setIsSending(false); }
    };

    const getUnreadColor = (count) => count > 10 ? '#dc2626' : count > 5 ? '#d97706' : '#52c41a';

    const selectedChatMeta = chatlist.find(c => c.id === selectedChatId);

    return (
        <div className="flex border rounded overflow-hidden" style={{ height: '420px' }}>
            {/* LEFT: Contact list */}
            <div className="flex flex-col border-r overflow-hidden" style={{ width: '185px', minWidth: '185px', backgroundColor: '#f9fafb' }}>
                <div className="px-2 py-1.5 border-b text-xs font-semibold text-gray-500 bg-white flex-none">
                    Suppliers ({filteredChats.length})
                </div>
                <div className="flex-1 overflow-y-auto">
                    {filteredChats.length === 0 ? (
                        <div className="flex items-center justify-center h-full text-xs text-gray-400 italic p-2 text-center">
                            No matching chats found in contact list
                        </div>
                    ) : filteredChats.map(chat => {
                        const isActive = chat.id === selectedChatId;
                        return (
                            <div
                                key={chat.id}
                                onClick={() => selectChat(chat.id)}
                                className="flex items-center gap-2 px-2 py-2 cursor-pointer border-b transition-colors duration-100 select-none"
                                style={{ backgroundColor: isActive ? '#dcf8c6' : 'white' }}
                            >
                                <img src={person_avatar} style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0 }} alt="DP" />
                                <div style={{ minWidth: 0, flex: 1 }}>
                                    <div className="flex items-center justify-between">
                                        <div className="truncate text-xs font-semibold" style={{ maxWidth: '100px' }}>{chat.name}</div>
                                        <Badge count={chat.unreadCount} size="small" style={{ backgroundColor: getUnreadColor(chat.unreadCount) }} />
                                    </div>
                                    <div className="truncate text-gray-400" style={{ fontSize: 10 }}>
                                        {chat.text || chat.whatsappId}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* RIGHT: Chat area */}
            <div className="flex flex-col flex-1 overflow-hidden">
                {/* Chat header */}
                {selectedChatMeta ? (
                    <div className="flex items-center justify-between px-3 py-1.5 border-b bg-white flex-none">
                        <div className="flex items-center gap-2">
                            <img src={person_avatar} style={{ width: 28, height: 28, borderRadius: '50%' }} alt="DP" />
                            <div>
                                <div className="text-xs font-semibold">{selectedChatMeta.name}</div>
                                <div className="text-gray-400" style={{ fontSize: 10 }}>{selectedChatMeta.whatsappId}</div>
                            </div>
                        </div>
                        <Button
                            size="small"
                            type="link"
                            icon={<ArrowRightOutlined />}
                            onClick={() => navigate('/whatsapp_messages')}
                            title="Open in WhatsApp Messages"
                            style={{ fontSize: 11, padding: '0 4px' }}
                        >
                            Open chat
                        </Button>
                    </div>
                ) : (
                    <div className="flex items-center px-3 py-2 border-b bg-white flex-none" style={{ height: 42 }}>
                        <span className="text-xs text-gray-400 italic">Select a supplier to view chat</span>
                    </div>
                )}

                {/* Messages */}
                <div ref={messageContainerRef} className="flex-1 overflow-y-auto p-2" style={{ backgroundColor: '#cacbcc' }}>
                    {!selectedChatId ? (
                        <div className="flex h-full items-center justify-center">
                            <div className="bg-zinc-800 text-white text-xs px-4 py-2 rounded-lg text-center">
                                Select a supplier to see their chat
                            </div>
                        </div>
                    ) : chatWindowLoading ? (
                        <div className="flex h-full items-center justify-center">
                            <img src={loading_circle_eclipse} style={{ width: 48 }} alt="loading" />
                        </div>
                    ) : (
                        <>
                            {/* Load older */}
                            <div
                                className={`flex justify-center py-1 mb-1 ${hasMore && !loadingOlder ? 'cursor-pointer opacity-100' : 'cursor-not-allowed opacity-30'}`}
                                onClick={hasMore && !loadingOlder ? loadOlderMessages : undefined}
                                title={hasMore ? 'Load older messages' : 'No older messages'}
                            >
                                {loadingOlder ? <img src={loading_circle_eclipse} style={{ width: 16, height: 16 }} /> : <PlusCircleOutlined style={{ fontSize: 14 }} />}
                            </div>

                            {messages.map(msg => (
                                msg.eventType === 'ticket' ? (
                                    <div key={msg.id} className="flex justify-center">
                                        <Divider className="text-xs">
                                            {msg.eventDescription + ' - ' + formatChatMessageTime(new Date(msg.created).getTime() / 1000)}
                                        </Divider>
                                    </div>
                                ) : (
                                    <div
                                        key={msg.id}
                                        ref={el => { if (el) messageRefsMap.current[msg.id] = el; else delete messageRefsMap.current[msg.id]; }}
                                        className="flex w-full"
                                        style={{ justifyContent: msg.owner || msg.finalText ? 'flex-end' : 'flex-start' }}
                                    >
                                        <div className={`whitespace-pre-wrap relative mt-1 mb-1 p-2 max-w-[75%] rounded-lg border shadow-sm text-xs transition-all duration-300
                                            ${msg.owner || msg.finalText
                                                ? 'bg-[#dcf8c6] rounded-br-none mr-1'
                                                : 'bg-white rounded-bl-none ml-1'}
                                            ${flashingId === msg.id ? 'ring-2 ring-[#8349e6] bg-[#ede7f6]' : ''}
                                        `}>
                                            {/* Image */}
                                            {msg.type === 'image' && (
                                                msg.data
                                                    ? <div className="relative mb-1">
                                                        <Image style={{ maxHeight: 180 }} className="rounded" src={msg.data} />
                                                        <div onClick={(e) => { e.stopPropagation(); handleDownload(msg.data_original, `img_${msg.id}.jpg`, msg.id); }}
                                                            className={`bg-black/70 text-white rounded-full flex items-center justify-center cursor-pointer hover:scale-110 transition-all`}
                                                            style={{ position: 'absolute', top: 6, right: 6, width: 26, height: 26, zIndex: 1 }}>
                                                            {downloadingId === msg.id ? <LoadingOutlined style={{ fontSize: 11 }} /> : <DownloadOutlined style={{ fontSize: 11 }} />}
                                                        </div>
                                                    </div>
                                                    : <div className="rounded bg-gray-200 animate-pulse mb-1" style={{ width: 150, height: 120 }} />
                                            )}

                                            {/* Video */}
                                            {msg.type === 'video' && (
                                                msg.data
                                                    ? <div className="relative mb-1">
                                                        <video src={msg.data} className="max-h-[180px] rounded" controls />
                                                        <div onClick={(e) => { e.stopPropagation(); handleDownload(msg.data_original, `vid_${msg.id}.mp4`, msg.id); }}
                                                            className="bg-black/70 text-white rounded-full flex items-center justify-center cursor-pointer hover:scale-110 transition-all"
                                                            style={{ position: 'absolute', top: 6, right: 6, width: 26, height: 26, zIndex: 1 }}>
                                                            {downloadingId === msg.id ? <LoadingOutlined style={{ fontSize: 11 }} /> : <DownloadOutlined style={{ fontSize: 11 }} />}
                                                        </div>
                                                    </div>
                                                    : <div className="rounded bg-gray-200 animate-pulse mb-1" style={{ width: 150, height: 150 }} />
                                            )}

                                            {/* Document */}
                                            {msg.type === 'document' && (
                                                <div className="flex items-center gap-2 mb-1 p-1.5 rounded bg-white/60 border">
                                                    <FileOutlined style={{ fontSize: 22, color: '#555' }} />
                                                    <div className="flex-1 text-xs text-gray-700 break-all line-clamp-2">{msg.text || 'Document'}</div>
                                                    <div onClick={(e) => { e.stopPropagation(); handleDownload(msg.data_original, msg.text || `doc_${msg.id}`, msg.id); }}
                                                        className="bg-black/70 text-white rounded-full flex items-center justify-center cursor-pointer hover:scale-110 transition-all flex-shrink-0"
                                                        style={{ width: 24, height: 24 }}>
                                                        {downloadingId === msg.id ? <LoadingOutlined style={{ fontSize: 10 }} /> : <DownloadOutlined style={{ fontSize: 10 }} />}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Audio */}
                                            {msg.type === 'audio' && (
                                                msg.data
                                                    ? <div className="flex items-center gap-1 mb-1">
                                                        <audio src={msg.data} controls style={{ height: 28, maxWidth: 180 }} />
                                                        <div onClick={(e) => { e.stopPropagation(); handleDownload(msg.data_original, `audio_${msg.id}.ogg`, msg.id); }}
                                                            className="bg-black/70 text-white rounded-full flex items-center justify-center cursor-pointer hover:scale-110 transition-all flex-shrink-0"
                                                            style={{ width: 24, height: 24 }}>
                                                            {downloadingId === msg.id ? <LoadingOutlined style={{ fontSize: 10 }} /> : <DownloadOutlined style={{ fontSize: 10 }} />}
                                                        </div>
                                                    </div>
                                                    : <div className="rounded bg-gray-200 animate-pulse mb-1" style={{ width: 180, height: 28 }} />
                                            )}

                                            {/* Reply context */}
                                            {msg.replySourceMessage && (
                                                <div className="flex border rounded bg-white/60 mb-1 overflow-hidden cursor-pointer hover:bg-white/90"
                                                    onClick={() => scrollToMessage(msg.replySourceMessage.id)}>
                                                    <div style={{ width: 3, backgroundColor: '#8349e6', flexShrink: 0 }} />
                                                    <div className="px-1.5 py-1">
                                                        <div className="text-[10px] font-semibold text-[#8349e6]">{msg.replySourceMessage.senderName || 'Quoted'}</div>
                                                        <div className="text-xs text-gray-700 line-clamp-2">{formatWhatsAppText(msg.replySourceMessage.text)}</div>
                                                    </div>
                                                </div>
                                            )}

                                            {formatWhatsAppText(msg.text || msg.finalText)}

                                            <div className="text-[9px] text-gray-500 text-right mt-0.5 leading-none">
                                                {msg.owner && msg.operatorName ? msg.operatorName + ' · ' : null}
                                                {formatChatMessageTime(msg.timestamp || new Date(msg.created).getTime() / 1000)}
                                                {msg.owner && msg.statusString ? <> · {renderStatus(msg.statusString)}</> : null}
                                            </div>
                                        </div>
                                    </div>
                                )
                            ))}
                            <div ref={scrollBottomRef} />
                        </>
                    )}
                </div>

                {/* Send box */}
                {selectedChatId && !chatWindowLoading && (
                    <div className="flex-none px-2 py-1.5 bg-white border-t flex items-end gap-1.5">
                        <div className="flex-1 bg-[#f0f2f5] rounded-xl px-3 py-1 border border-transparent focus-within:border-[#7bfc68] transition-all duration-200">
                            <textarea
                                rows={1}
                                value={messageText}
                                onChange={e => setMessageText(e.target.value)}
                                disabled={isSending}
                                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                                placeholder="Type a message…"
                                className="w-full outline-none resize-none bg-transparent pt-1.5 text-xs text-gray-700"
                                style={{ minHeight: 28, maxHeight: 100 }}
                            />
                        </div>
                        <Button
                            type="primary"
                            shape="circle"
                            icon={isSending ? <LoadingOutlined /> : <SendOutlined />}
                            onClick={handleSend}
                            disabled={!messageText.trim() || isSending}
                            size="small"
                            style={{
                                width: 36, height: 36, flexShrink: 0,
                                backgroundColor: messageText.trim() ? '#075e54' : '#d1d1d1',
                                borderColor: 'transparent',
                            }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default QueryChatMini;
